/**
 * Shared column-sorting behavior for the listing tables.
 *
 * The API returns rows in relevance order, and that stays the default — sorting
 * is opt-in. Clicking a column cycles ascending -> descending -> back to API
 * order. Sorting applies to the whole result set before pagination, not just the
 * visible page.
 *
 * A view using this mixin must provide:
 *   - a `sortSource` computed returning the unsorted rows
 *   - a `sortValue(row, key)` method returning the comparable value for a column
 *   - `columns` entries carrying a `sortKey` for whichever columns should sort
 *
 * It gets back `sortedItems` (feed it to AppPagination), `sort` state to bind to
 * AppTable, and `mobileSort`/`applyMobileSort` for the small-screen dropdown.
 */
export default {
  data() {
    return {
      // Null key/order means "leave the API's relevance order alone".
      sort: {
        key: null,
        order: null,
      },
    };
  },
  computed: {
    sortableColumns() {
      return (this.columns || []).filter((column) => column.sortKey);
    },
    mobileSort() {
      return this.sort.key ? `${this.sort.key}:${this.sort.order}` : "";
    },
    sortedItems() {
      const rows = this.sortSource || [];
      if (!this.sort.key || !this.sort.order) return rows;
      const direction = this.sort.order === "asc" ? 1 : -1;
      // Array.sort is stable, so equally-valued rows keep the API's order.
      return [...rows].sort((a, b) => {
        const aValue = (this.sortValue(a, this.sort.key) ?? "")
          .toString()
          .trim();
        const bValue = (this.sortValue(b, this.sort.key) ?? "")
          .toString()
          .trim();
        // Blanks always sit at the bottom, whichever way we are sorting.
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        return (
          direction *
          aValue.localeCompare(bValue, undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );
      });
    },
  },
  methods: {
    // Clicking a column cycles ascending -> descending -> back to API order.
    toggleSort(key) {
      if (this.sort.key !== key) {
        this.sort = { key, order: "asc" };
      } else if (this.sort.order === "asc") {
        this.sort = { key, order: "desc" };
      } else {
        this.sort = { key: null, order: null };
      }
    },
    applyMobileSort(value) {
      if (!value) {
        this.sort = { key: null, order: null };
        return;
      }
      const [key, order] = value.split(":");
      this.sort = { key, order };
    },
  },
};
